import { createContext, useState, useEffect } from "react";
import axios from "axios";

/**
 * Authentication Context
 * Provides authentication state and functions to login, logout, and check authentication.
 */

const AuthContext = createContext({ isAuthenticated: false});

/**
 * AuthProvider Component
 * - Wraps the application and provides authentication state.
 * - Manages user login state and handles authentication requests.
 *
 * @param {Object} props - React props
 * @param {JSX.Element} props.children - Child components that will have access to AuthContext
 * @returns {JSX.Element} - The provider component wrapping all children
 */

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:3000/"
  : "https://api.hillerodfrimenighed.dk/";

  /**
   * Calls the validate endpoint to check if the user is has a valid
   * authentication
   */

  useEffect(() => {
    axios.get(`${API_URL}user/auth/validate`, { withCredentials: true })
      .then(() => setIsAuthenticated(true))
      .catch((error) => {setIsAuthenticated(false)
        console.error("Error message:", error.response.data?.message || "No message")
      })
      .finally(() => setLoading(false));
  }, [API_URL]);

  /**
   * Handles user login
   * - Sends login request with google to the backend
   */
  const login = () => {
    window.location.href = `${API_URL}user/login`;
  };
  

  /**
   * Handles user logout
   * - Sends logout request to the backend
   *
   * @async
   * @returns {Promise<void>}
   */
  const logout = async () => {
    await axios.post(`${API_URL}user/logout`, {}, { withCredentials: true });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
